import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowUturnDownIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';

const Returns = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isAdminOrFinance = user?.role === 'admin' || user?.role === 'finance';
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    returnStatus: '',
    returnReason: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewData, setReviewData] = useState({
    id: '',
    status: 'approved',
    reason: 'expired',
    notes: ''
  });
  const [formData, setFormData] = useState({
    agent: '',
    shopName: '',
    shopLocation: '',
    notes: ''
  });
  const [items, setItems] = useState([
    { product: '', quantity: '', unitPrice: '', returnReason: 'expired', expiryDate: '', batchNumber: '' }
  ]);

  useEffect(() => {
    fetchData();
  }, [page, limit, filters]);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get('/api/transactions', {
          params: {
            type: 'return',
            page,
            limit,
            search: filters.search || undefined,
            returnStatus: filters.returnStatus || undefined,
            returnReason: filters.returnReason || undefined,
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined
          }
        })
      ];
      if (user?.role === 'agent' || user?.role === 'depot_sales' || user?.role === 'admin' || user?.role === 'finance') {
        requests.unshift(axios.get('/api/products', { params: { limit: 1000 } }));
      }
      if (user?.role === 'admin' || user?.role === 'finance') {
        requests.unshift(axios.get('/api/users/agents'));
      }

      const responses = await Promise.all(requests);
      const returnsRes = responses[responses.length - 1];

      if (user?.role === 'admin' || user?.role === 'finance') {
        setAgents(responses[0].data || []);
        setProducts(responses[1].data.products || []);
      } else if (user?.role === 'agent' || user?.role === 'depot_sales') {
        setProducts(responses[0].data.products || []);
      }
      setReturns(returnsRes.data.transactions || []);
      setTotalPages(returnsRes.data.totalPages || 1);
      setTotal(returnsRes.data.total || 0);
    } catch (fetchError) {
      console.error('Error fetching returns data:', fetchError);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { product: '', quantity: '', unitPrice: '', returnReason: 'expired', expiryDate: '', batchNumber: '' }
    ]);
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        items: items.map((item) => ({
          product: item.product,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          returnReason: item.returnReason,
          expiryDate: item.expiryDate,
          batchNumber: item.batchNumber
        })),
        notes: formData.notes
      };

      // Include shop data only for agents
      if (user?.role === 'agent' || user?.role === 'depot_sales') {
        payload.shop = {
          name: formData.shopName,
          location: formData.shopLocation
        };
      } else {
        // For admin/finance, shop is optional but still use a default
        payload.shop = {
          name: formData.shopName || 'Direct Return',
          location: formData.shopLocation || ''
        };
      }

      // Include agent if admin/finance is submitting
      if (isAdminOrFinance && formData.agent) {
        payload.agent = formData.agent;
      }

      const response = await axios.post('/api/transactions/return', payload, {
        timeout: 30000
      });

      // Show success message from backend
      if (response.data.message) {
        alert(response.data.message);
      }

      setFormData({
        agent: '',
        shopName: '',
        shopLocation: '',
        notes: ''
      });
      setItems([{ product: '', quantity: '', unitPrice: '', returnReason: 'expired', expiryDate: '', batchNumber: '' }]);

      fetchData();
    } catch (submitError) {
      console.error('Error recording return:', submitError);
      if (submitError.code === 'ECONNABORTED') {
        setError('Request timeout. Please check if the return was recorded before trying again.');
      } else {
        setError(
          submitError.response?.data?.message || 'Failed to record return. Please check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openReviewModal = (entry, status) => {
    setReviewData({
      id: entry._id,
      status,
      reason: entry.returnReviewReason || 'expired',
      notes: ''
    });
    setActionError('');
    setReviewModalOpen(true);
  };

  const handleReviewReasonChange = (event) => {
    const { value } = event.target;
    setReviewData((prev) => ({ ...prev, reason: value }));
  };

  const handleReviewNotesChange = (event) => {
    const { value } = event.target;
    setReviewData((prev) => ({ ...prev, notes: value }));
  };

  const submitReview = async (event) => {
    event.preventDefault();
    setActionLoadingId(reviewData.id);
    setActionError('');
    try {
      await axios.put(`/api/transactions/return/${reviewData.id}/status`, {
        status: reviewData.status,
        reason: reviewData.reason,
        notes: reviewData.notes
      });
      fetchData();
      setReviewModalOpen(false);
    } catch (actionError) {
      console.error('Error updating return status:', actionError);
      setActionError(
        actionError.response?.data?.message || 'Failed to update return status.'
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this return record?')) return;
    try {
      await axios.delete(`/api/transactions/${entryId}`);
      fetchData();
    } catch (deleteError) {
      console.error('Error deleting return:', deleteError);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Returns</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Record returned milk and keep shop debts accurate.
          </p>
        </div>
        <div className="flex items-center gap-2 text-primary-600">
          <ArrowUturnDownIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Return Entry</span>
        </div>
      </div>

      {(user?.role === 'agent' || user?.role === 'depot_sales' || isAdminOrFinance) && (
        <div className="card bg-secondary-50">
          <div className="card-header">
            <h3 className="text-lg font-medium text-secondary-900">Record Return</h3>
          </div>
          <div className="card-body">
            <div className="mb-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">Return Processing:</p>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li><span className="font-semibold">Unsold items:</span> Added back to depot stock immediately</li>
                <li><span className="font-semibold">Expired/Damaged/Mismatch:</span> Recorded as factory credit</li>
              </ul>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
            {isAdminOrFinance && (
              <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 border-2 border-primary-200">
                <label className="block text-sm font-bold text-secondary-900 mb-2">Agent *</label>
                <select
                  name="agent"
                  value={formData.agent}
                  onChange={handleChange}
                  className="input-field"
                  required
                >
                  <option value="">Select agent</option>
                  {agents.map((agent) => (
                    <option key={agent._id} value={agent._id}>
                      {agent.name} ({agent.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-4 border-2 border-primary-200">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-base font-bold text-secondary-900">Return Items</label>
                <button type="button" className="btn-secondary text-sm py-2 px-4 flex items-center gap-2" onClick={addItem}>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add item
                </button>
              </div>
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Product *</label>
                        <select
                          value={item.product}
                          onChange={(e) => handleItemChange(index, 'product', e.target.value)}
                          className="input-field"
                          required
                        >
                          <option value="">Select product</option>
                          {products.map((product) => (
                            <option key={product._id} value={product._id}>
                              {product.name} ({product.size})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Quantity *</label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          className="input-field"
                          min="1"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Unit Price *</label>
                        <input
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                          className="input-field"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Batch Number *</label>
                        <input
                          type="text"
                          value={item.batchNumber}
                          onChange={(e) => handleItemChange(index, 'batchNumber', e.target.value)}
                          className="input-field"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Return Reason *</label>
                        <select
                          value={item.returnReason}
                          onChange={(e) => handleItemChange(index, 'returnReason', e.target.value)}
                          className="input-field"
                          required
                        >
                          <option value="expired">Expired</option>
                          <option value="damaged">Damaged</option>
                          <option value="unsold">Unsold</option>
                          <option value="mismatch">Mismatch</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700">Expiry Date *</label>
                        <input
                          type="date"
                          value={item.expiryDate}
                          onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                          className="input-field"
                          required
                        />
                      </div>
                    </div>
                    {items.length > 1 && (
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          className="text-red-600 text-sm font-medium hover:text-red-700"
                          onClick={() => removeItem(index)}
                        >
                          Remove Item
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {(user?.role === 'agent' || user?.role === 'depot_sales') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Shop Name *</label>
                  <input
                    type="text"
                    name="shopName"
                    value={formData.shopName}
                    onChange={handleChange}
                    className="input-field"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700">Shop Location</label>
                  <input
                    type="text"
                    name="shopLocation"
                    value={formData.shopLocation}
                    onChange={handleChange}
                    className="input-field"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-secondary-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="input-field"
                rows="3"
              />
            </div>

              {error && (
                <div className="rounded-lg bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200 p-4 shadow-sm">
                  <div className="text-sm font-semibold text-red-700">{error}</div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : `Save ${items.length} Return${items.length > 1 ? 's' : ''}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card bg-secondary-50">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search returns..."
              className="input-field md:col-span-2"
            />
            <select
              name="returnStatus"
              value={filters.returnStatus}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              name="returnReason"
              value={filters.returnReason}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All reasons</option>
              <option value="expired">Expired</option>
              <option value="damaged">Damaged</option>
              <option value="unsold">Unsold</option>
              <option value="mismatch">Mismatch</option>
              <option value="other">Other</option>
            </select>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="input-field"
            />
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="input-field"
            />
          </div>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-header">
          <h3 className="text-lg font-medium text-secondary-900">Recent Returns</h3>
        </div>
        <div className="card-body">
          {returns.length === 0 ? (
            <p className="text-sm text-secondary-500">No returns recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Shop
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                      Status
                    </th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                        Reviewed By
                      </th>
                    )}
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-100">
                  {returns.map((entry) => (
                    <tr key={entry._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {entry.shop?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {entry.product?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {entry.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          entry.returnStatus === 'approved'
                            ? 'bg-success-100 text-success-800'
                            : entry.returnStatus === 'rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {entry.returnStatus || 'pending'}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                          {entry.returnReviewedBy ? (
                            <div>
                              <div className="font-medium">{entry.returnReviewedBy.name}</div>
                              <div className="text-xs text-secondary-500">
                                {entry.returnReviewedAt
                                  ? new Date(entry.returnReviewedAt).toLocaleString()
                                  : ''}
                              </div>
                              {entry.returnReviewNotes && (
                                <div className="text-xs text-secondary-500">
                                  {entry.returnReviewNotes}
                                </div>
                              )}
                              {entry.returnReviewReason && (
                                <div className="text-xs text-secondary-500">
                                  Reason: {entry.returnReviewReason}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-secondary-400">Pending</span>
                          )}
                        </td>
                      )}
                      {isAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                          {entry.returnStatus === 'pending' ? (
                            <div className="flex gap-2">
                              <button
                                className="text-success-600 hover:text-success-700 font-medium"
                                onClick={() => openReviewModal(entry, 'approved')}
                                disabled={actionLoadingId === entry._id}
                              >
                                Approve
                              </button>
                              <button
                                className="text-red-600 hover:text-red-700 font-medium"
                                onClick={() => openReviewModal(entry, 'rejected')}
                                disabled={actionLoadingId === entry._id}
                              >
                                Reject
                              </button>
                              <button
                                className="text-secondary-600 hover:text-secondary-800 font-medium"
                                onClick={() => handleDelete(entry._id)}
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <span className="text-xs text-secondary-400">Processed</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {actionError && (
            <div className="mt-3 rounded-md bg-red-50 p-3 text-sm text-red-700">
              {actionError}
            </div>
          )}
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-secondary-100 bg-white px-6 py-4 text-sm text-secondary-600 md:flex-row">
            <div>
              Showing page {page} of {totalPages} · {total} returns
            </div>
            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="input-field h-9"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>
              <button
                className="btn-secondary"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {reviewModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">
                {reviewData.status === 'approved' ? 'Approve Return' : 'Reject Return'}
              </h3>
              <form onSubmit={submitReview} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Reason</label>
                  <select
                    value={reviewData.reason}
                    onChange={handleReviewReasonChange}
                    className="input-field"
                  >
                    <option value="expired">Expired</option>
                    <option value="damaged">Damaged</option>
                    <option value="unsold">Unsold</option>
                    <option value="mismatch">Mismatch</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    value={reviewData.notes}
                    onChange={handleReviewNotesChange}
                    className="input-field"
                    rows="3"
                    placeholder={
                      reviewData.status === 'rejected'
                        ? 'Notes required for rejection'
                        : 'Add notes (optional)'
                    }
                  />
                </div>

                {actionError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {actionError}
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setReviewModalOpen(false)}
                    disabled={actionLoadingId === reviewData.id}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={actionLoadingId === reviewData.id}
                  >
                    {actionLoadingId === reviewData.id ? 'Saving...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Returns;
